/* ==============================================================================
   Convite pendente na URL
   ==============================================================================
   O relato foi "convites não está funcionando", com a URL
   `https://concord.fuxar.online/convite/tZ8UAX5gSm0f` aberta e o app mostrando a
   tela inicial vazia. Não era o convite que estava quebrado: o servidor gera esse
   link corretamente e as rotas de prévia e aceite existem e funcionam. O cliente
   simplesmente NUNCA LIA o caminho da URL.

   Três problemas que este módulo resolve
   ======================================

   1. **O código tem de ser lido ANTES do React montar.**
      O `nginx` serve o `index.html` para qualquer caminho (é uma SPA), então o
      app carrega — mas nada olhava `location.pathname`. Ler num `useEffect`
      funcionaria, porém depois de o `AppShell` já ter decidido o que renderizar,
      produzindo um piscar da tela inicial antes do convite.

   2. **O código tem de SOBREVIVER ao login.**
      Quem clica num convite sem estar autenticado passa por login ou cadastro.
      O estado do React é perdido nesse caminho quando há OIDC, que sai da
      página e volta. `sessionStorage` atravessa navegação e recarregamento, e
      morre quando a aba fecha — que é exatamente o tempo de vida certo para
      isto.

   3. **A URL tem de ser LIMPA imediatamente.**
      Deixar `/convite/CÓDIGO` na barra significa que qualquer recarregamento
      tenta entrar de novo, e que o código fica no histórico do navegador e em
      qualquer captura de tela que a pessoa compartilhe. `replaceState` troca o
      endereço sem recarregar e sem criar entrada no histórico — voltar não
      retorna ao convite.

   Por que `sessionStorage` e não `localStorage`
   ============================================
   `localStorage` é compartilhado entre TODAS as abas e persiste indefinidamente.
   Um convite recusado hoje reapareceria amanhã, numa aba diferente, sem nada na
   tela explicando de onde veio.
   ============================================================================== */

const STORAGE_KEY = 'concord.convite-pendente';

/**
 * Formato do código, igual ao que o servidor gera.
 *
 * `members.ts` usa 10 bytes em base64url sem padding, o que dá 13 ou 14
 * caracteres do alfabeto `A-Za-z0-9_-`. A validação aqui é DELIBERADAMENTE
 * frouxa no comprimento (8 a 64): o servidor é quem decide se o código existe,
 * e um formato apertado no cliente recusaria um código válido caso o gerador
 * mude — falhando com "código inválido" sobre um convite perfeitamente bom.
 *
 * O que este regex realmente impede é o caminho da URL virar entrada arbitrária:
 * sem ele, `/convite/<script>` chegaria a uma chamada de API e a uma mensagem de
 * erro na tela com o conteúdo do caminho dentro.
 */
const CODE_SHAPE = /^[A-Za-z0-9_-]{8,64}$/;

/**
 * Lê o código do caminho e o guarda. Chame UMA vez, antes de renderizar.
 *
 * Devolve o código encontrado, ou `null`. Idempotente: chamar de novo depois de
 * a URL ter sido limpa devolve o que já estava guardado.
 */
export function captureInviteFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  /*
   * Dois prefixos aceitos.
   *
   * `/convite/` é o que o servidor gera. `/invite/` existe porque é o que todo
   * mundo digita por reflexo e o que uma ferramenta de terceiros montaria — e
   * aceitar os dois custa uma linha, contra uma pessoa concluindo que o link
   * está quebrado.
   */
  const match = /^\/(?:convite|invite)\/([^/?#]+)/.exec(window.location.pathname);

  if (match) {
    const raw = match[1] ?? '';
    // `decodeURIComponent` pode lançar com um `%` solto no caminho.
    let code = raw;
    try {
      code = decodeURIComponent(raw);
    } catch {
      code = raw;
    }

    /*
     * A URL é limpa mesmo quando o código é INVÁLIDO.
     *
     * Manter `/convite/lixo` na barra faria todo recarregamento repetir a mesma
     * falha, e a pessoa não teria como voltar ao app a não ser editando a URL à
     * mão.
     */
    window.history.replaceState(null, '', '/');

    if (CODE_SHAPE.test(code)) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, code);
      } catch {
        /*
         * `sessionStorage` pode lançar: modo privado de alguns navegadores, cota
         * cheia, ou política que bloqueia armazenamento. Nesse caso o convite
         * ainda funciona NESTA renderização — só não sobrevive ao login.
         */
      }
      return code;
    }
    return null;
  }

  return readPendingInvite();
}

/** Código guardado, se houver. */
export function readPendingInvite(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored !== null && CODE_SHAPE.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Descarta o convite guardado.
 *
 * Chamado ao ACEITAR e ao RECUSAR. Nos dois casos a decisão foi tomada, e um
 * convite que reaparece depois de recusado é um bug que a pessoa não tem como
 * contornar.
 */
export function clearPendingInvite(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Sem armazenamento não havia nada guardado para remover.
  }
}

/**
 * Extrai o código de algo que a pessoa COLOU.
 *
 * Aceita o código puro, a URL completa, ou a URL com barra no fim. Foi pedido
 * junto do botão de ingressar: *"falta o botão de ingressar em um servidor e
 * digitar o código"* — e quem recebe um convite copia o link inteiro, não o
 * código. Exigir só o código faria a colagem óbvia falhar.
 */
export function parseInviteInput(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;

  // A última parte não vazia do caminho. Cobre URL com e sem barra final, com
  // querystring e com fragmento.
  const withoutQuery = trimmed.split(/[?#]/)[0] ?? '';
  const segments = withoutQuery.split('/').filter((part) => part.length > 0);
  const candidate = segments[segments.length - 1] ?? '';

  return CODE_SHAPE.test(candidate) ? candidate : null;
}
